import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useSeoMeta } from '@/hooks/useSeoMeta';

const BASE_URL = import.meta.env.VITE_SITE_URL ?? 'https://roameurope.blog';

const schemaJson = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact roameurope.blog',
  description: 'Get in touch with the roameurope.blog team. Send us a message with questions, suggestions, or feedback.',
  url: `${BASE_URL}/contact`,
  publisher: {
    '@type': 'Organization',
    name: 'roameurope.blog',
    url: BASE_URL,
  },
});

export default function ContactPage() {
  useSeoMeta({
    title: 'Contact Us — roameurope.blog',
    description: 'Get in touch with the roameurope.blog team. Send us a message with questions, suggestions, or feedback about our relationship advice articles.',
    url: '/contact',
    type: 'website',
    tags: ['contact roameurope.blog', 'relationship advice', 'get in touch'],
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7bs8iaqofe8vli40ov0', { method: 'POST', body: data });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: schemaJson }}
      />
      <Navbar />
      <main className="pt-28 pb-20 min-h-screen">
        <div className="max-w-2xl mx-auto px-4 md:px-8">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" style={{ fontFamily: "'Lora', serif" }}>Contact Us</h1>
          <p className="text-stone-500 mb-8 text-sm">Have a question, suggestion, or just want to say hello? We'd love to hear from you.</p>
          {submitted ? (
            <div className="bg-rose-50 border border-rose-100 rounded-lg p-8 text-center">
              <i className="ri-checkbox-circle-line text-4xl text-rose-500 mb-3 block" />
              <p className="font-semibold text-stone-800">Message sent!</p>
              <p className="text-sm text-stone-500 mt-1">We'll get back to you as soon as possible.</p>
            </div>
          ) : (
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Name</label>
                <input name="name" required className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400" placeholder="Your name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
                <input name="email" type="email" required className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400" placeholder="your@email.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Message</label>
                <textarea name="message" required maxLength={500} rows={5} className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-rose-400 resize-none" placeholder="Your message..." />
              </div>
              <button type="submit" disabled={loading} className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-3 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
